/**
 * Inventory Engine - Handles stock and inventory logic
 * Separated from UI components for modularity and testability
 */

/**
 * Check if item is in stock
 * @param {number} quantity - Available quantity
 * @param {number} requested - Requested quantity
 * @param {boolean} continueSellingOutOfStock - Allow overselling
 * @returns {object} Stock status
 */
export const checkStock = (quantity, requested = 1, continueSellingOutOfStock = false) => {
  const available = parseInt(quantity) || 0;
  const need = parseInt(requested) || 1;

  if (continueSellingOutOfStock) {
    return {
      inStock: true,
      available,
      canFulfill: true,
      message: available > 0 ? 'In Stock' : 'Available on order',
    };
  }

  if (available <= 0) {
    return {
      inStock: false,
      available: 0,
      canFulfill: false,
      message: 'Out of Stock',
    };
  }

  if (available < need) {
    return {
      inStock: true,
      available,
      canFulfill: false,
      message: `Only ${available} available`,
    };
  }

  return {
    inStock: true,
    available,
    canFulfill: true,
    message: available < 10 ? `Only ${available} left` : 'In Stock',
  };
};

/**
 * Get stock level status
 * @param {number} quantity - Current quantity
 * @param {number} lowThreshold - Low stock threshold (default 10)
 * @returns {object} Stock level info
 */
export const getStockLevel = (quantity, lowThreshold = 10) => {
  const qty = parseInt(quantity) || 0;

  if (qty <= 0) {
    return { level: 'out', color: 'red', label: 'Out of Stock' };
  }

  if (qty <= lowThreshold) {
    return { level: 'low', color: 'orange', label: 'Low Stock' };
  }

  return { level: 'normal', color: 'green', label: 'In Stock' };
};

/**
 * Calculate variant inventory total
 * @param {Array} variants - Array of variant objects with inventory
 * @returns {object} Aggregated inventory info
 */
export const calculateVariantInventory = (variants = []) => {
  if (!variants || variants.length === 0) {
    return { total: 0, byVariant: [], hasLowStock: false };
  }

  const byVariant = variants.map((v, index) => ({
    index,
    type: v.type || `Variant ${index + 1}`,
    values: v.values || '',
    inventory: parseInt(v.inventory) || 0,
    status: getStockLevel(v.inventory),
  }));

  const total = byVariant.reduce((sum, v) => sum + v.inventory, 0);
  const hasLowStock = byVariant.some(
    (v) => v.status.level === 'low' || v.status.level === 'out'
  );

  return { total, byVariant, hasLowStock };
};

/**
 * Validate SKU format
 * @param {string} sku - SKU to validate
 * @param {Array} existingSKUs - List of existing SKUs to check uniqueness
 * @returns {object} Validation result
 */
export const validateSKU = (sku, existingSKUs = []) => {
  if (!sku || sku.trim() === '') {
    return { valid: true, warning: 'SKU not provided (auto-generation recommended)' };
  }

  const trimmedSKU = sku.trim().toUpperCase();

  // Check format (alphanumeric with dashes/underscores)
  if (!/^[A-Z0-9\-_]+$/.test(trimmedSKU)) {
    return {
      valid: false,
      error: 'SKU must contain only letters, numbers, dashes, and underscores',
    };
  }

  // Check length
  if (trimmedSKU.length < 3 || trimmedSKU.length > 50) {
    return { valid: false, error: 'SKU must be 3-50 characters' };
  }

  // Check uniqueness
  if (existingSKUs.includes(trimmedSKU)) {
    return { valid: false, error: 'SKU already exists' };
  }

  return { valid: true, normalizedSKU: trimmedSKU };
};

/**
 * Generate SKU from product info
 * @param {string} category - Product category
 * @param {string} title - Product title
 * @param {number} randomSuffix - Add random suffix length (default 4)
 * @returns {string} Generated SKU
 */
export const generateSKU = (category = '', title = '', randomSuffix = 4) => {
  const catPart = category
    .substring(0, 3)
    .toUpperCase()
    .replace(/[^A-Z]/g, '');

  const titlePart = title
    .substring(0, 4)
    .toUpperCase()
    .replace(/[^A-Z]/g, '');

  const randomPart = Math.random()
    .toString(36)
    .substring(2, 2 + randomSuffix)
    .toUpperCase();

  return `${catPart || 'PRD'}-${titlePart || 'ITEM'}-${randomPart}`;
};

/**
 * Calculate reorder suggestion
 * @param {number} currentStock - Current stock level
 * @param {number} averageDailySales - Average daily sales
 * @param {number} leadTimeDays - Supplier lead time in days
 * @returns {object} Reorder recommendation
 */
export const calculateReorderPoint = (currentStock, averageDailySales, leadTimeDays = 7) => {
  const stock = parseInt(currentStock) || 0;
  const dailySales = parseFloat(averageDailySales) || 0;
  const leadTime = parseInt(leadTimeDays) || 7;

  // Reorder point = (Average daily sales × Lead time) + Safety stock
  const safetyStock = dailySales * 3; // 3 days safety stock
  const reorderPoint = Math.ceil(dailySales * leadTime + safetyStock);
  const daysUntilStockout = dailySales > 0 ? Math.floor(stock / dailySales) : Infinity;

  return {
    reorderPoint,
    shouldReorder: stock <= reorderPoint,
    daysUntilStockout,
    suggestedOrderQuantity: Math.max(0, reorderPoint * 2 - stock), // Order 2x reorder point
  };
};

/**
 * Format inventory display
 * @param {number} quantity - Quantity to display
 * @param {boolean} showExact - Show exact number or range
 * @returns {string}
 */
export const formatInventoryDisplay = (quantity, showExact = false) => {
  const qty = parseInt(quantity) || 0;

  if (qty <= 0) return 'Out of Stock';
  if (showExact) return `${qty} in stock`;
  if (qty < 5) return `Only ${qty} left`;
  if (qty < 20) return `Less than 20 in stock`;
  if (qty < 50) return `Limited stock`;
  return 'In Stock';
};

export default {
  checkStock,
  getStockLevel,
  calculateVariantInventory,
  validateSKU,
  generateSKU,
  calculateReorderPoint,
  formatInventoryDisplay,
};
